import {appConfig, gaConfig} from '@stencil/hotels-config';
// SELECTORS
import {
  aboutPropertySelectorFn,
  goSafeSegmentDataSelectorFn,
  hotelPolicyDataSelector,
  hotelsAmenitiesSelector,
  roomDataHermesSelector,
  similarHotelsSelector,
  showSafetyHygieneSectionSelector,
} from '@stencil/hotels-details-reducer/src/selectors';
import debounce from '@stencil/hotels-utils/src/debounce';
// import { searchWidgetId } from '../../../../utils/common-utils';
import detailsPageClickGaPush from '@stencil/hotels-utils/src/details-page-click-ga-push';
import getDetailsActiveScrollStatus from '@stencil/hotels-utils/src/get-details-active-scroll-status';
import {
  detailsNavigationIds,
  detailsNavigationIdsAndValues,
  getDetailsNavigationIdsAndValuesObjectById,
} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';

import {
  hasQnaDataSelector,
  snapDataSelector,
} from '@stencil/hotels-ugc-redux/src/hotels-ugc-selectors';

// import { searchWidgetId } from '../../../../utils/common-utils';

// CONFIG
import {scrollToEl} from '@stencil/hotels-utils/src/scroll-into-view-details';
import React, {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
// HOOKS
import {useHistory, useLocation} from 'react-router-dom';
// STYLES
import * as S from './Navigation.styles';
import getVerticalAscendingOrderOfElements from '@stencil/hotels-utils/src/get-vertical-ascending-order-of-elements';

const {DetailsGAClickEventActions} = gaConfig;

const {detailsNavigationElementId, onLoadScrollToElementTimeoutDetails} = appConfig;

const Item: React.FC<{children: React.ReactNode; active?: boolean}> = ({children, active}) => (
  <li>
    <S.TextWrapperSpan {...(active ? {className: 'active'} : {})}>{children}</S.TextWrapperSpan>
  </li>
);

const Navigation = () => {
  const [activeMenuItemId, setActiveMenuItemId] = useState('');
  const [hideNavigation, setHideNavigation] = useState(false);
  const location = useLocation();
  const {hash, pathname, search} = location;
  const history = useHistory();
  const {all: amenitiesList} = useSelector(hotelsAmenitiesSelector);
  const hasQnaData = useSelector(hasQnaDataSelector);
  const roomsDataFromHermes = useSelector(roomDataHermesSelector);
  const snapData = useSelector(snapDataSelector);
  const similarHotelsData = useSelector(similarHotelsSelector);
  const {cards: goSafeSegmentData = []} = useSelector(goSafeSegmentDataSelectorFn);
  const showSafetyHygiene = useSelector(showSafetyHygieneSectionSelector);

  const {hotelPolicyList = []} = useSelector(hotelPolicyDataSelector);
  const aboutPropertyData = useSelector(aboutPropertySelectorFn);

  const thereAreSimilarHotels = !!similarHotelsData?.list.slice(1).length;

  const thereAreRooms = !!roomsDataFromHermes.length;

  const navigationRef = useRef<HTMLElement | null>(null);

  const {giDataExists = true, taDataExists} = snapData;

  useEffect(() => {
    // set active menu item from url hash, which helps with styles
    if (!hash) return;

    const allDataForPagePresent = !!amenitiesList.length && !!roomsDataFromHermes.length;

    /**
     * If there's a hash on details page, that means
     * we intend to be scrolled down to a particular element.
     * In order to make the user see that they are being scrolled down
     * we wait for 'onLoadScrollToElementTimeoutDetails' amount of timeout before scrolling.
     * If we do not do this, then the scroll starts in a flash (without the user being
     * able to see where the scrolling started from).
     *
     * TL;DR
     *
     * This time out is for user interaction and perceivability enhancement
     */
    let timerId = setTimeout(() => {
      // scroll to the element of which id is equal to hash in location url

      if (
        !document.querySelector(location.hash) ||
        /**ideally this should only check for data related to the container corresponding
         * to the hash as its id. But this should be fine as well
         */
        !allDataForPagePresent
      ) {
        return;
      }

      setActiveMenuItemId(hash);
      scrollToEl(location.hash);
      return () => clearTimeout(timerId);
    }, onLoadScrollToElementTimeoutDetails);

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [amenitiesList, roomsDataFromHermes, similarHotelsData, hotelPolicyList]);

  // https://css-tricks.com/sticky-smooth-active-nav/
  useEffect(() => {
    const allNavlinks = Array.from<HTMLAnchorElement>(
      document.querySelectorAll('#details-navigation a'),
    );

    // being super safe
    if (!allNavlinks.length) {
      return;
    }

    function updateActiveNavLink() {
      allNavlinks.forEach(link => {
        let section: HTMLElement = document.querySelector(link.hash);

        if (!section) {
          return;
        }

        if (getDetailsActiveScrollStatus(section)) {
          setActiveMenuItemId(link.hash);
        }
      });

      hideNavigationWhenFAQActive();
    }

    function hideNavigationWhenFAQActive() {
      let faqsSection = document.getElementById(detailsNavigationIds.faqs) as HTMLElement;
      if (!faqsSection) return;

      if (getDetailsActiveScrollStatus(faqsSection)) {
        setHideNavigation(true);
      } else {
        setHideNavigation(false);
      }
    }

    const debouncedFn = debounce(updateActiveNavLink, 150);

    window.addEventListener('scroll', updateActiveNavLink);

    return () => {
      window.addEventListener('remove', updateActiveNavLink);
      debouncedFn.cancel();
    };
  }, [amenitiesList, roomsDataFromHermes, similarHotelsData, hotelPolicyList, hasQnaData]);

  const navSectionOrderedIds = useRef([]);

  useEffect(() => {
    navSectionOrderedIds.current = getVerticalAscendingOrderOfElements(
      Array.from(
        Object.values(detailsNavigationIdsAndValues).map(e => document.querySelector(e.id)),
      ),
    ).map(el => el.id);
  }, [amenitiesList, roomsDataFromHermes, similarHotelsData, hotelPolicyList]);

  if (hideNavigation) {
    return null;
  }

  return (
    <S.NavigationStyleContainer ref={navigationRef} id={detailsNavigationElementId}>
      <S.StyledWrapper>
        <S.MenuList>
          {/* sync this up with current active and scroll with current same page link urls */}

          {navSectionOrderedIds.current.map(_id => {
            const id = `#${_id}`;

            // only used for mweb, hence return null
            if (id === detailsNavigationIdsAndValues.overview.id) {
              return null;
            }
            /**
             * TO PALAK/ANYONE:
             *
             * please also add handlings for guest reviews, RnR and About property
             * or any other data which is shown in navigation. Also please also add
             * these dependencies in the scroll handling useEffect above to update
             * the active link of the navigation menu, and also the URL scroll useEffect handling above it
             */

            // check if there's data to show for the navigation
            if (id === detailsNavigationIdsAndValues.amenities.id && !amenitiesList.length) {
              return null;
            }

            if (id === detailsNavigationIdsAndValues.goSafe.id && !goSafeSegmentData.length)
              return null;

            if (id === detailsNavigationIdsAndValues.safetyHygiene.id && !showSafetyHygiene)
              return null;

            if (id === detailsNavigationIdsAndValues.rooms.id && !thereAreRooms) {
              return null;
            }

            if (
              id === detailsNavigationIdsAndValues.guestReviews.id &&
              !(giDataExists || taDataExists)
            ) {
              return null;
            }

            if (id === detailsNavigationIdsAndValues.QnA.id && !hasQnaData) {
              return null;
            }

            if (
              id === detailsNavigationIdsAndValues?.aboutProperty?.id &&
              !aboutPropertyData.length
            ) {
              return null;
            }

            if (id === detailsNavigationIdsAndValues.similarHotels.id && !thereAreSimilarHotels) {
              return null;
            }

            if (id === detailsNavigationIdsAndValues.hotelPolicies.id && !hotelPolicyList.length) {
              return null;
            }

            if (id === detailsNavigationIdsAndValues.faqs.id) {
              return null;
            }

            return (
              <Item key={id} active={!!(activeMenuItemId && activeMenuItemId === id)}>
                <S.ItemLinkTo
                  href={id}
                  active={!!(activeMenuItemId && activeMenuItemId === id)}
                  data-testid={`navigation-header-cta-${id}`}
                  /**
                   * NOTE: not extracting this function in a separate function
                   * because it's a small function definition and is beneficial
                   * to understand what is happening here itself. And also because
                   * https://cdb.reacttraining.com/react-inline-functions-and-performance-bdff784f5578?gi=970e2972fe2e
                   */
                  onClick={(e: Event) => {
                    /**
                     * because we want to control scroll behavior
                     * we prevent a tag to 'snapping' to the desired element
                     * and instead manually push the history to (location + hash)
                     */
                    e.preventDefault();

                    detailsPageClickGaPush({
                      eventAction: DetailsGAClickEventActions.nav[id],
                    });

                    // current location + hash
                    history.push(pathname + search + id);

                    // set active menu item which helps with styles
                    // setActiveMenuItemId(id);

                    // scroll to desired element if it's there
                    scrollToEl(id);
                  }}
                >
                  {getDetailsNavigationIdsAndValuesObjectById(id) &&
                    getDetailsNavigationIdsAndValuesObjectById(id).value}
                </S.ItemLinkTo>
              </Item>
            );
          })}
        </S.MenuList>
      </S.StyledWrapper>
    </S.NavigationStyleContainer>
  );
};

export default Navigation;
